import { Edit, Lock, NavigateNext } from '@mui/icons-material';
import {
  Breadcrumbs,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type { FC } from 'react';
import { useContext, useEffect, useState, useMemo } from 'react';
import { generatePath, NavLink, useParams } from 'react-router-dom';
import {
  BrandKey,
  GetPageCategoriesQuery,
} from '../../../../../../__generated__/queries-web';
import { paths } from '../../../../../../routing/Router/paths';
import { auth } from '../../../../../../utils/auth';
import { CategoriesContext } from '../../../../categories.context';
import { DataTestIdListPage } from '../../../../tests/constants';
import type { Category } from '../../../../types';
import { navigateTo } from '../../../../utils';
import { MAX_SEGMENTS_BREADCRUMBS } from './constants';
import { useStyles } from './styles';

interface BreadCrumb {
  id: string;
  isActive?: boolean;
  pathname?: string;
  title: string | JSX.Element;
}

const formatTitleMap: Record<string, string> = {
  event: 'Évènements',
  channel: 'Channel',
  guide: 'Guide pratique',
  category: 'Catégories',
};

interface BreadCrumbsProps {
  breadcrumbsData?: GetPageCategoriesQuery;
}

export const BreadCrumbs: FC<BreadCrumbsProps> = ({ breadcrumbsData }) => {
  const { brandKey, format } = useContext(CategoriesContext);
  const routeParams = useParams();
  const [breadCrumbs, setBreadCrumbs] = useState<BreadCrumb[]>([]);
  const theme = useTheme();

  const classes = useStyles({
    isRootCategoryActive: false,
  });

  const isAllowedToInteractWithRootCategory = auth.user?.isSuperAdmin();

  // Get active category ID from route params
  const activeCategoryId = useMemo(() => {
    const { lvl1, lvl2, lvl3, lvl4, lvl5 } = routeParams;
    return lvl5 || lvl4 || lvl3 || lvl2 || lvl1 || undefined;
  }, [routeParams]);

  // Process data from parent component
  const processedData = useMemo(() => {
    const categoriesData = breadcrumbsData?.categories;

    if (!categoriesData || categoriesData.length === 0) {
      return {
        allCategories: [],
        activeCategory: null,
      };
    }

    // Find active category directly
    let activeCategory: Category | null = null;
    if (activeCategoryId && categoriesData.length > 0) {
      const found = categoriesData.find(
        (cat) => cat.id === activeCategoryId && cat.brandKey === brandKey,
      );
      if (found) {
        // Build the category path manually
        activeCategory = {
          ...found,
          path: found.title,
        } as Category;
      }
    }

    return {
      allCategories: categoriesData,
      activeCategory,
    };
  }, [breadcrumbsData?.categories, activeCategoryId, brandKey]);

  const {
    allCategories,
    activeCategory: currentActiveCategory,
  } = processedData;

  const categories = useMemo(() => {
    // Only process data when we have data
    if (!allCategories || allCategories.length === 0) {
      return [];
    }

    // Process categories directly instead of using external method
    return allCategories.map((category) => ({
      ...category,
      path: category.title,
    }));
  }, [allCategories]);

  useEffect(() => {
    const { brandKey: routeBrandKey, format: routeFormat } = routeParams;

    if (!routeFormat) return;

    // Don't build breadcrumbs if we have no data
    if (categories.length === 0) {
      return;
    }
    const newBreadCrumbs: BreadCrumb[] = [];

    const EditButton = ({ categoryId }: { categoryId: string }) => (
      <Tooltip title="Modifier le template">
        <IconButton
          color="primary"
          size="small"
          onClick={() => {
            const search = new URLSearchParams({
              from: 'Breadcrumbs',
              categoryId,
            }).toString();

            const pathname = `${generatePath(paths.PAGE_EDIT, {
              brandKey: routeBrandKey,
              format: routeFormat,
            })}/${categoryId}`;

            navigateTo({
              pathname,
              opts: { search },
            });
          }}
        >
          <Edit sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    );

    // Use active category from the hook
    const currentCategory = currentActiveCategory;

    // Always show BONE level 1 category as the root

    const level1Category = categories.find((cat: any) => {
      const matches =
        cat.level === 1 &&
        cat.format.toLowerCase() === routeFormat?.toLowerCase() &&
        cat.brandKey === BrandKey.BONE;
      return matches;
    });

    if (level1Category) {
      // Level 1 is always grey (non-active) but editable for admins
      const isAtRootLevel = !currentCategory;
      const isLevel1TheActiveCategory =
        currentCategory?.id === level1Category.id; // Level 1 is the active category

      // eslint-disable-next-line fp/no-mutating-methods
      newBreadCrumbs.push({
        id: level1Category.id,
        isActive: false, // Always grey, never active
        pathname: isAtRootLevel
          ? undefined
          : `${generatePath(paths.PAGE_LIST, {
              brandKey: routeBrandKey,
              format: routeFormat,
            })}/${level1Category.id}`,
        title: (
          <Stack direction="row" alignItems="center" spacing={1}>
            <span className={classes.wrapperBreadCrumbRootCategory}>
              <Chip color="primary" label="Racine" size="small" />
              {level1Category.title}
            </span>
            {isLevel1TheActiveCategory &&
              isAllowedToInteractWithRootCategory && (
                <EditButton categoryId={level1Category.id} />
              )}
          </Stack>
        ),
      });
    } else if (!isAllowedToInteractWithRootCategory) {
      // For non-admin: show format title
      // eslint-disable-next-line fp/no-mutating-methods
      newBreadCrumbs.push({
        id: 'root',
        pathname: undefined,
        title: (
          <span className={classes.wrapperBreadCrumbRootCategory}>
            <Chip
              color="primary"
              label="Racine"
              size="small"
              deleteIcon={<Lock />}
              onDelete={() => {}}
            />
            {formatTitleMap[routeFormat?.toLowerCase() || ''] || routeFormat}
          </span>
        ),
      });
    }

    // Build breadcrumb path from current category
    if (currentCategory) {
      // Build the full path by finding all parent categories using recursion
      const buildBreadcrumbPath = (category: Category): Category[] => {
        const buildPathRecursive = (
          current: Category,
          path: Category[] = [],
        ): Category[] => {
          if (!current || current.level <= 1) {
            return path;
          }

          // Find parent category
          const parent = categories.find(
            (cat) => cat.id === current.parent?.id,
          ) as Category;

          if (parent) {
            return buildPathRecursive(parent, [current, ...path]);
          }

          return [current, ...path];
        };

        return buildPathRecursive(category);
      };

      const categoryPath = buildBreadcrumbPath(currentCategory);

      // Add each category in the path (excluding level 1 which is already added above)
      const pathBreadCrumbs = categoryPath.map((category, index) => {
        const isCurrentCategory = index === categoryPath.length - 1;
        const isClickable = !isCurrentCategory && category.level > 1;
        const showEditButton = isCurrentCategory;

        // Build path for this level using category IDs
        const pathSegments = categoryPath
          .slice(0, index + 1)
          .map((cat) => cat.id);

        return {
          id: category.id,
          isActive: isCurrentCategory,
          pathname: isClickable
            ? generatePath(paths.PAGE_LIST, {
                brandKey: routeBrandKey,
                format: routeFormat,
                lvl1: pathSegments[0] || undefined,
                lvl2: pathSegments[1] || undefined,
                lvl3: pathSegments[2] || undefined,
                lvl4: pathSegments[3] || undefined,
                lvl5: pathSegments[4] || undefined,
              })
            : undefined,
          title: (
            <Stack direction="row" alignItems="center" spacing={1}>
              <span>{category.title}</span>
              {showEditButton && <EditButton categoryId={category.id} />}
            </Stack>
          ),
        };
      });

      // eslint-disable-next-line fp/no-mutating-methods
      newBreadCrumbs.push(...pathBreadCrumbs);
    } else {
      // No active category, show default level 2 category
      const level2Category = categories.find(
        (cat) =>
          cat.level === 2 &&
          cat.format.toLowerCase() === routeFormat?.toLowerCase() &&
          cat.brandKey === routeBrandKey,
      );

      if (level2Category) {
        // eslint-disable-next-line fp/no-mutating-methods
        newBreadCrumbs.push({
          id: level2Category.id,
          isActive: true,
          pathname: generatePath(paths.PAGE_LIST, {
            brandKey: routeBrandKey,
            format: routeFormat,
          }),
          title: (
            <Stack direction="row" alignItems="center" spacing={1}>
              <span>{level2Category.title}</span>
              <EditButton categoryId={level2Category.id} />
            </Stack>
          ),
        });
      }
    }

    setBreadCrumbs(newBreadCrumbs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    categories,
    routeParams,
    currentActiveCategory,
    isAllowedToInteractWithRootCategory,
    format,
    brandKey,
  ]);

  return (
    <div className={classes.wrapper}>
      <Breadcrumbs
        aria-label="breadcrumb"
        maxItems={MAX_SEGMENTS_BREADCRUMBS}
        separator={<NavigateNext fontSize="small" />}
      >
        {breadCrumbs.length > 0
          ? breadCrumbs.map((breadCrumb: BreadCrumb, index: number) => {
              const shouldShowNavLink =
                breadCrumb.pathname && !breadCrumb.isActive;

              if (shouldShowNavLink && breadCrumb.pathname) {
                return (
                  <NavLink
                    className={classes.breadCrumbLink}
                    data-testid={DataTestIdListPage.LIST_BREADCRUMBS_LINK}
                    key={breadCrumb.id || index}
                    to={breadCrumb.pathname}
                    style={{
                      color: theme.palette.grey[500],
                    }}
                  >
                    {breadCrumb.title}
                  </NavLink>
                );
              }

              return (
                <Typography
                  className={classes.breadCrumbLink}
                  data-testid={DataTestIdListPage.LIST_BREADCRUMBS_TEXT}
                  key={breadCrumb.id || index}
                  sx={{
                    color: breadCrumb.isActive
                      ? theme.palette.primary.main
                      : theme.palette.grey[500],
                  }}
                >
                  {breadCrumb.title}
                </Typography>
              );
            })
          : null}
      </Breadcrumbs>
    </div>
  );
};
